import {Component, OnDestroy, OnInit} from '@angular/core';
import {NbMediaBreakpointsService, NbMenuService, NbSidebarService, NbThemeService} from '@nebular/theme';

import {UserData} from '../../../@core/data/users';
import {LayoutService} from '../../../@core/utils';
import {map, takeUntil} from 'rxjs/operators';
import {Subject, Observable} from 'rxjs';
import {RippleService} from '../../../@core/utils/ripple.service';
import {AuthService} from '../../../auth/auth.service';
import {Router} from '@angular/router';
import {MerchantService} from '../../../pages/merchant/merchant.service';
import {Merchant} from '../../../@services/gen';
import packageJson from '../../../../../package.json';

@Component({
  selector: 'ngx-header',
  styleUrls: ['./header.component.scss'],
  templateUrl: './header.component.html',
})
export class HeaderComponent implements OnInit, OnDestroy {
  private destroy$: Subject<void> = new Subject<void>();
  public readonly materialTheme$: Observable<boolean>;
  userPictureOnly: boolean = false;
  hideMenuOnClick: boolean = false;
  user: any;
  username: string;
  merchant: string;
  merchantLogo: string;
  version: string = packageJson.version;

  themes = [
    {
      value: 'default',
      name: 'Light',
    },
    {
      value: 'dark',
      name: 'Dark',
    },
    {
      value: 'cosmic',
      name: 'Cosmic',
    },
    {
      value: 'corporate',
      name: 'Corporate',
    },
    {
      value: 'material-light',
      name: 'Material Light',
    },
    {
      value: 'material-dark',
      name: 'Material Dark',
    },
  ];

  currentTheme = 'corporate';
  currentUser$;
  userrole;

  userMenu = [
    {title: 'Change Account', data: 'changeAccount'},
    {title: 'Log out', data: 'logout'},
  ];

  public constructor(
    private sidebarService: NbSidebarService,
    private menuService: NbMenuService,
    private themeService: NbThemeService,
    private userService: UserData,
    private layoutService: LayoutService,
    private breakpointService: NbMediaBreakpointsService,
    private rippleService: RippleService,
    public authService: AuthService,
    private router: Router,
    public merchantService: MerchantService
  ) {
    this.materialTheme$ = this.themeService.onThemeChange().pipe(
      map((theme) => {
        const themeName: string = theme?.name || '';
        return themeName.startsWith('material');
      })
    );
  }

  ngOnInit() {
    this.currentUser$ = this.authService.getCurrentUser();
    this.currentTheme = this.themeService.currentTheme;

    const userRole = this.currentUser$.role; // Assuming the inventoryOrder object has a "role" property

    this.userService
      .getUsers()
      .pipe(takeUntil(this.destroy$))
      .subscribe((users: any) => (this.user = users.nick));

    const {xl} = this.breakpointService.getBreakpointsMap();
    this.themeService
      .onMediaQueryChange()
      .pipe(
        map(([, currentBreakpoint]) => currentBreakpoint.width < xl),
        takeUntil(this.destroy$)
      )
      .subscribe((isLessThanXl: boolean) => {
        this.userPictureOnly = isLessThanXl;
        this.hideMenuOnClick = isLessThanXl;
      });

    this.themeService
      .onThemeChange()
      .pipe(
        map(({name}) => name),
        takeUntil(this.destroy$)
      )
      .subscribe((themeName) => {
        this.currentTheme = themeName;
        this.rippleService.toggle(themeName?.startsWith('material'));
      });

    this.authService.currentUser$.subscribe((user) => {
      this.user = user;
      if (this.user) {
        this.user.name = user?.username;
      }
      this.username = this.user?.name;
    });

    if (userRole !== 'super-admin') {
      this.merchantService.currentMerchant$.subscribe((merchant) => {
        if (merchant) {
          this.merchant = merchant.storeName;
          this.merchantLogo = merchant.merchantLogo;
        }
      });
    } else {
      this.merchant = this.username;
      this.merchantLogo = null;
    }

    this.userrole = userRole;

    this.menuService.onItemClick().subscribe(({item}) => {
      if (item.data === 'logout') {
        this.logout();
      }
      if (item.data === 'changeAccount') {
        this.changeMerchant();
      }
      if (this.hideMenuOnClick) {
        this.sidebarService.collapse('menu-sidebar');
      }
    });
  }

  logout() {
    this.authService.logout().subscribe(() => {
      this.router.navigate(['/login']);
    });
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  changeTheme(themeName: string) {
    this.themeService.changeTheme(themeName);
  }

  toggleSidebar(): boolean {
    this.sidebarService.toggle(true, 'menu-sidebar');
    this.layoutService.changeLayoutSize();

    return false;
  }

  navigateHome() {
    this.menuService.navigateHome();
    return false;
  }

  changeMerchant() {
    if (this.authService.getCurrentUser() && this.authService.getCurrentUser()?.merchants?.length > 1) {
      this.merchantService.openDialogMerchant();
    }
  }

  canChangeMerchant() {
    if (this.authService.getCurrentUser() && this.authService.getCurrentUser()?.merchants?.length > 1) {
      return true;
    }

    return false;
  }

  protected readonly Merchant = Merchant;
}
